/* eslint-disable unicorn/no-array-for-each */
/* eslint-disable no-undef */
// @ts-nocheck
/* eslint-disable no-console */
import moment from 'moment';
import {
	WSAPIConstructor,
	Room as SocketRoom,
} from '@knitiv/api-client-javascript';
import Collection from '~/pages/chat/models/collection';
import MessageManager from '~/pages/chat/models/message-manager';
import Message from '~/pages/chat/models/message';
import Room from '~/pages/chat/models/room';
import RepresentationManager from '~/pages/chat/models/representation-manager';
import Defaults from '~/scripts/constants/defaults';
import APIV5 from '~/models/api';
import KDate from '~/models/kdate';
import EVENTS from './models/events';
import ChatConstants from './chat.constant';
import { ws } from '../../services/websocket.service';

/**
 * @typedef {'online'|'offline'|'connecting'|'errored'} ChatStatus
 */

/**
 * @property UP
 * @property DOWN
 * @readonly
 * @enum {number}
 */
const fetchDirection = {
	UP: 0,
	DOWN: 1,
};

export default class ChatService {
	/* @ngInject */
	/**
	 * @param {any} User
	 * @param {angular.IQService} $q
	 * @param {WebsocketService} WebsocketService
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {angular.ILocationService} $location
	 * @param {any} Notification
	 * @param {any} NodeService
	 * @param {angular.ITimeoutService} $timeout
	 */
	constructor(
		User,
		$q,
		WebsocketService,
		$rootScope,
		$location,
		Notification,
		NodeService,
		$timeout,
	) {
		this.$q = $q;
		/** @type {WSAPIConstructor} */
		this.WebsocketService = WebsocketService.socket;
		this.$rootScope = $rootScope;
		this.$location = $location;
		this.Notification = Notification;
		this.NodeService = NodeService;
		this.Defaults = Defaults;
		this.$timeout = $timeout;

		// Variables

		/** @type {LocalUser | * | undefined} */
		this.currentUser = undefined;

		/** @type {string | undefined} */
		this.currentToken = undefined;

		this.userLang = User.getContext().lang[0];

		this.messageManager = new MessageManager(this.$q);
		/** @type {Collection<string, Room>} */
		this.rooms = new Collection();
		/** @type {Collection<string, string>} */
		this.statuses = new Collection();

		this.representations = new RepresentationManager(this.$q, this.$rootScope);

		this.attachments = new Collection();

		this.settingsRef = null;
		this.debugMode = false;

		/** @type {string} */
		this.recipient = '';
		/** @type {ChatStatus} */
		this.status = 'offline';
		this.isEnabled = false;

		/** @type {SocketRoom} */
		this.chatListener = null;

		$rootScope.$on('socket:ready', (event, socket) => {
			this.d('Socket Ready');

			this.currentUser = $rootScope.context;
			this.currentToken = this.currentUser?.token?.id;

			this.WebsocketService = socket;
			this.isEnabled = true;

			this.registerNotifications();
			this.registerSockets();
		});

		this.isReady = false;

		// handle notification recipient change
		// /?to=K_NODE;QG-K_NODE;FRED
		this.$rootScope.$on('$locationChangeStart', async () => {
			const isChatPage = this.$location.path().startsWith(ChatConstants.chatURL);
			const { to } = this.$location.search();

			if (isChatPage && to) {
				await this.changeRoom(to);
				this.$timeout(() => {
					$location.search('to', null);
				});
			}
			return false;
		});
	}

	// --- Functions ------------------------------------------------------------

	d(...arguments_) {
		if (this.debugMode) {
			console.info('[DEBUG]', ...arguments_);
		}
	}

	/**
	 *
	 * @param {Room} room
	 * @returns
	 */
	async importRoom(room) {
		this.d('Importing room into cache');
		// Create a room
		const newRoom = new Room(room, this.currentUser.person.kid);
		// Initialize it
		const initializedRoom = await newRoom.init();
		// console.log('initializedRoom', initializedRoom);
		this.representations.set(initializedRoom.receiver, initializedRoom.receiver);
		if (!initializedRoom.sender) {
			// One to One conversation is not available to current user
			return undefined;
		}
		return initializedRoom;
	}

	/**
	 * @async
	 * @param roomsData
	 */
	async setRooms(roomsData) {
		this.d('Loading all rooms');
		const promises = [];

		// For each room object
		for (const room of Object.values(roomsData)) {
			promises.push(this.importRoom(room));
		}

		const rooms = await Promise.all(promises);

		await this.representations.fetchAll();

		this.rooms = new Collection();
		for (const room of rooms) {
			if (room) {
				this.rooms.set(room.ref, room);
			}
		}

		return this.rooms;
	}

	/**
	 *
	 * @returns {Collection<string, Room>}
	 */
	getRecentsRoom() {
		// this.d('Fetching recent rooms');
		return this.rooms.filter(([, room]) => room?.isRecent);
	}

	/**
	 *
	 * @param {string} roomReference
	 * @returns {Array<Message>}
	 */
	getMessagesFromRoom(roomReference) {
		// this.d(`Getting messages from room ${roomReference}`);
		return this.messageManager
			.toArrayValues()
			.filter(message => message.room_kid === roomReference)
			.sort((a, b) => moment.utc(a.create_date).diff(moment.utc(b.create_date)));
	}

	/**
	 * Order kids for a room
	 *
	 * @param {string} key
	 * @param {string} user
	 * @returns {string}
	 */
	// eslint-disable-next-line class-methods-use-this
	orderRoomIds(key, user) {
		return [key, user].sort().join('-');
	}

	async markAsSeen(messageReference) {
		this.d('Marking message as read');
		const message = this.messageManager.get(messageReference);
		if (this.currentUser.profil.kid !== message.from_profil_kid) {
			await this.setMessageStatus(message, 'seen');
		}
	}

	/**
	 * Remove a message
	 *
	 * @param {string} messageReference
	 */
	removeMessage(messageReference) {
		this.d('Removing message', messageReference);
		this.messageManager.delete(messageReference);
	}

	/**
	 * Retrieve the amount of unread messages from a specific room
	 *
	 * @param {string} roomId - The ID of the room to retrieve
	 * @returns {number}
	 */
	fetchUnreadMessages(roomId) {
		if (roomId === undefined) {
			return 0;
		}
		this.d('Fetching unread messages from', roomId);
		/** @type {Room} */
		const room = this.rooms.get(roomId);
		if (room.isRecent) {
			const { lastAccess } = room;
			const messagesFromRoom = this.getMessagesFromRoom(roomId);
			room.lastMessageText = messagesFromRoom.at(-1)?.text ?? '';
			const messages = messagesFromRoom.filter(message => message.from_profil_kid !== this.currentUser.profil.kid
          && message.room_kid !== this.recipient
          && moment.utc(message.create_date).isAfter(moment.utc(lastAccess)));
			room.unreadMessages = messages.length;
		}
		else {
			room.unreadMessages = 0;
		}
		return room.unreadMessages;
	}

	totalUnreadNotificationsCount() {
		// calculate unread message number
		let totalUnreadNotificationsCount = 0;
		// console.log('this.rooms', this.rooms);
		for (const [, room] of this.rooms) {
			const { unreadMessages } = room;

			totalUnreadNotificationsCount += unreadMessages;
		}
		return totalUnreadNotificationsCount;
	}

	/**
	 * Change current room
	 *
	 * @async
	 * @param {string} [to] - The kid to use as the new recipient
	 * @param {boolean} skipInitialFetch
	 * @returns {Promise<Array<Message>>}
	 */
	async changeRoom(to, skipInitialFetch = false) {
		this.d(`Changing room to ${to}`);
		if (!to) {
			this.recipient = '';
			return [];
		}

		if (to === this.recipient) {
			return [];
		}

		// Set last access of previous recipient
		if (this.recipient && this.rooms.get(this.recipient)) {
			this.rooms.get(this.recipient).lastAccess = moment.utc().format(ChatConstants.dateFormat);
		}

		this.rooms.get(to).lastAccess = moment.utc().format(ChatConstants.dateFormat);

		// Set all unselected but the selected one
		for (const [key, conversation] of this.rooms.entries()) {
			conversation.isSelected = key === to;
		}

		this.recipient = to;
		try {
			if (!this.rooms.get(to).initialFetchDone && !skipInitialFetch) {
				await this.fetchNewMessages(to, fetchDirection.UP);
			}

			this.fetchUnreadMessages(this.recipient);
			// @ts-ignore
			return this.saveWorkspace();
		}
		catch (error) {
			console.error('An error has occurred', error);
			return [];
		}
	}

	saveWorkspace() {
		this.d('Saving settings');
		const rooms = this.getRecentsRoom();

		const savedRooms = {};

		for (const [roomKey, room] of rooms.entries()) {
			savedRooms[roomKey] = {
				lastAccess: room.lastAccess,
				isSelected: room.isSelected,
			};
		}

		// Update
		return this.settingsUpdate(this.settingsRef, {
			// data is the column name
			data: JSON.stringify({
				rooms: savedRooms,
				lastAction: moment.utc().format(ChatConstants.dateFormat),
			}),
		});
	}

	/**
	 *
	 * Chat Helpers
	 *
	 */
	/**
	 * @param {Partial<import('@knitiv/api-client-javascript').ListInput>} customParameters
	 * @returns {Promise<*>}
	 */
	// eslint-disable-next-line class-methods-use-this
	async messageList(customParameters) {
		return this.$q.when(
			APIV5.instance.list({
				elt_kid: 'KCHATMSG;0',
				...customParameters,
				kparam: {
					module: 'chat',
					kdate: KDate.now().asString(),
				},
			}, { id: 'chat-service.message-list' }),
		);
	}

	/**
	 *
	 * @param {string} reference
	 * @param {*} customParameters
	 * @returns {Promise<Chat.Message>}
	 */
	async messageGet(reference, customParameters) {
		return this.$q.when(
			APIV5.instance.get({
				elt_kid: reference,
				...customParameters,
				kparam: {
					module: 'chat',
					kdate: KDate.now().asString(),
				},
			}),
		);
	}

	/**
	 *
	 * @param {*} data
	 * @returns {Chat.Message}
	 */
	// eslint-disable-next-line class-methods-use-this
	async messageCreate(data) {
		return this.$q.when(
			APIV5.instance.set({
				elt_kid: 'KCHATMSG;_NR_001',
				data,
				kparam: {
					module: 'chat',
					kdate: KDate.now().asString(),
				},
			}, {
				id: 'chat-service.message-create',
			}),
		);
	}

	/**
	 *
	 * @param {string} kid
	 * @param {Omit<Chat.File, 'data'>} infos
	 * @param {Chat.File['data']} data
	 */
	async setFileState(kid, infos, data) {
		const eltKid = kid ?? 'KCHATFILE;_NR_001';
		return this.$q.when(
			APIV5.instance.set({
				elt_kid: eltKid,
				data: {
					...infos,
					data: JSON.stringify(data),
				},
				kparam: {
					module: 'chat',
					kdate: KDate.now().asString(),
				},
			}),
		);
	}

	/**
	 *
	 * @param {*} reference
	 * @param {*} data
	 * @returns {Promise<{ elt_kid: string; } &
	 * Record<string, { data: Promise<Chat.Message> }>
	 * }
	 */
	async messageUpdate(reference, data) {
		return this.$q.when(APIV5.instance.set({
			elt_kid: reference,
			data,
			kparam: {
				module: 'chat',
				kdate: KDate.now().asString(),
			},
		}));
	}

	// eslint-disable-next-line class-methods-use-this
	async messageDelete(element) {
		return this.$q.when(APIV5.instance.del(element));
	}

	/**
	 * @param {Partial<import('@knitiv/api-client-javascript').ListInput>} customParameters
	 * @returns {Promise<*>}
	 */
	// eslint-disable-next-line class-methods-use-this
	async settingsList(customParameters) {
		return this.$q.when(APIV5.instance.list({
			elt_kid: 'KCHATPERSON;0',
			...customParameters,
			kparam: {
				module: 'chat',
				kdate: KDate.now().asString(),
			},
		}, { id: 'chat-service.settings-list' }));
	}

	/**
	 * @param {Partial<import('@knitiv/api-client-javascript').ListInput>} [customParameters]
	 * @returns {Promise<*>}
	 */
	// eslint-disable-next-line class-methods-use-this
	async roomList(customParameters) {
		return this.$q.when(APIV5.instance.list({
			elt_kid: 'KCHATROOM;0',
			...customParameters,
			kparam: {
				module: 'chat',
				kdate: KDate.now().asString(),
			},
		}, { id: 'chat-service.room-list' }));
	}

	/**
	 * @param data
	 * @returns {Promise<import('@knitiv/api-client-javascript').SetOutput<Room>>}
	 */
	// eslint-disable-next-line class-methods-use-this
	async roomCreate(data) {
		return this.$q.when(APIV5.instance.set({
			elt_kid: 'KCHATROOM;_NR_001',
			data,
			kparam: {
				module: 'chat',
				kdate: KDate.now().asString(),
			},
		}));
	}

	// eslint-disable-next-line class-methods-use-this
	async settingsCreate(data) {
		return this.$q.when(APIV5.instance.set({
			elt_kid: 'KCHATPERSON;_NR_001',
			data,
			kparam: {
				module: 'chat',
				kdate: KDate.now().asString(),
			},
		}));
	}

	// eslint-disable-next-line class-methods-use-this
	async settingsUpdate(reference, data) {
		return this.$q.when(APIV5.instance.set({
			elt_kid: reference,
			data,
			kparam: {
				module: 'chat',
				kdate: KDate.now().asString(),
			},
		}));
	}

	getCurrentRoom() {
		return this.rooms.get(this.recipient);
	}

	/**
	 * @async
	 * @param {string} roomReference
	 * @param {fetchDirection} direction
	 */
	async fetchNewMessages(roomReference, direction) {
		this.d(`Fetching new messages from ${roomReference}`);
		// const room = this.rooms.get(to);
		const room = this.rooms.get(roomReference);
		if (!room.upFetchDate) {
			room.upFetchDate = moment.utc().format(ChatConstants.dateFormat);
		}
		room.isLoading = true;
		/** @type {import('@knitiv/api-client-javascript').ListFilterExpressions} */
		const sqlDirection = direction === fetchDirection.UP ? 'SQL_<' : 'SQL_>';
		const orderDirection = direction === fetchDirection.UP ? 'DESC' : 'ASC';
		const data = await this.messageList({
			packet_size: ChatConstants.messagesPerPacket,
			filter: {
				room_kid: roomReference,
				[sqlDirection]: {
					create_date: room.upFetchDate,
				},
			},
			orderby: { create_date: orderDirection },
		});
		const messages = Object.values(data.elts);
		if (!messages || messages.length === 0) {
			room.isLoading = false;
			room.noMoreMessagesUp = true;
			return [];
		}
		if (messages.length < ChatConstants.messagesPerPacket) {
			room.noMoreMessagesUp = true;
		}

		const promises = [];
		const representationsToFetch = new Set();
		for (const message of Object.values(messages)) {
			const result = this.messageManager.import(message);
			promises.push(result);

			representationsToFetch.add(message.from_profil_kid);
		}

		const representationsPromises = [];
		for (const repre of representationsToFetch) {
			const repreFetch = this.representations.fetch(repre);
			representationsPromises.push(repreFetch);
		}

		const resolvedMessages = await Promise.all(promises);
		await Promise.all(representationsPromises);

		// Update upFetchDate date to the older one
		const sortedMessages = resolvedMessages.sort(
			(a, b) => moment.utc(a.create_date).diff(moment.utc(b.create_date)),
		);
		const firstMessage = sortedMessages[0];
		// const lastMessage = sortedMessages[sortedMessages.length - 1];
		// room.lastMessageText = lastMessage.text;
		const roomToChange = this.rooms.get(roomReference);
		if (firstMessage) {
			roomToChange.upFetchDate = moment.utc(firstMessage.create_date).format(ChatConstants.dateFormat);
		}

		if (!roomToChange.initialFetchDone) {
			roomToChange.initialFetchDone = true;

			this.$rootScope.$broadcast('chat:initial-room-fetch');
		}
		room.isLoading = false;
		return resolvedMessages;
	}

	async search(searchData) {
		console.log('searchData', searchData);

		const data = await this.messageList({
			packet_size: ChatConstants.messagesPerPacket,
			filter: {
				SQL_AND: {

					SQL_IN: {
						room_kid: [...this.getRecentsRoom().keys()].map(room => `"${room}"`).join(','),
					},

					SQL_LIKE: {
						text: `%${searchData.text}%`,
					},
				},
			},
			orderby: { create_date: 'DESC' },
		});
		console.log('data', data);
		const elements = data.elts;

		const promises = [];
		for (const element of Object.values(elements)) {
			promises.push(this.messageManager.import(element));
		}
		const loadedElements = await Promise.all(promises);
		return loadedElements;
	}

	/**
	 * Fetch messages before and after another message
	 *
	 * @param message
	 * @param amount
	 * @returns {Promise<any>}
	 */
	async queryMessageContext(message, amount = 25) {
		const datas = await Promise
			.all([
				this.messageList({
					packet_size: amount,
					filter: {
						room_kid: message.room_kid,
						'SQL_<': {
							create_date: message.create_date,
						},
					},
					orderby: { create_date: 'DESC' },
				}),
				this.messageList({
					packet_size: amount,
					filter: {
						room_kid: message.room_kid,
						'SQL_>': {
							create_date: message.create_date,
						},
					},
					orderby: { create_date: 'ASC' },
				}),
			]);
		const merged = { ...datas[0].elts, ...datas[1].elts };
		const promises = [];
		for (const element of Object.values(merged)) {
			promises.push(this.messageManager.import(element));
		}
		const loadedElements = await Promise.all(promises);
		// this.rooms.get(message.room_kid).upFetchDate =
		return loadedElements;
	}

	/**
	 * Resolve an id to it's corresponding representation
	 *
	 * @param {string} id - The id to resolve
	 * @param {'user'|'group'} type - The type of data to resolve
	 * @returns {string}
	 */
	resolve(id, type) {
		switch (type) {
		case 'group': {
			return this.rooms.get(id).repre;
		}

		case 'user': {
			return this.representations.get(id);
		}

		default: {
			return id;
		}
		}
	}

	/**
	 * Delete a message permanently
	 *
	 * @param {Message} message
	 * @returns {Promise<Message>}
	 */
	async deleteMessage(message) {
		const { ref } = message;
		const { room_kid: roomKid } = message;

		// eslint-disable-next-line no-restricted-globals
		if (confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
			const deleted = await this.messageDelete(ref);
			console.log('deleted', deleted);
			console.log('ref', ref);
			const messageReference = deleted.elt_kid[0];

			/**
			 * @type {Room}
			 */
			const room = this.rooms.get(roomKid);

			room.listener.sendAll({ type: EVENTS.CHAT.MESSAGE.DELETE, ref: messageReference });
			return message;
		}
		throw new Error('cancel');
	}

	/**
	 * @typedef MessageSend
	 * @property {string} text
	 * @property {*} file
	 * @property {*} isReplying
	 * @property {*} isEditing
	 * @property {string} room
	 * @property {string} from
	 */
	/**
	 * Send a message
	 *
	 * @async
	 * @param {MessageSend} messageSendObj
	 */
	async send({
		text,
		file,
		isEditing,
		isReplying,
		room,
		from = this.currentUser.profil.kid,
	}) {
		console.log('room', room);
		this.d('Sending message', text);

		if (text === '' && !file) {
			throw new Error('Empty text');
		}

		if (isEditing) {
			const result = await this.messageUpdate(isEditing.ref, {
				text,
				update_date: moment.utc().format(ChatConstants.dateFormat),
			});
			const message = result[result.elt_kid].data;
			console.log('send message update', message);

			/** @type {Room} */
			const roomObject = this.rooms.get(message.room_kid);

			return roomObject.listener.sendAll({
				type: EVENTS.CHAT.MESSAGE.UPDATE,
				message,
			});
		}

		/** @type {Message} */
		const message = {
			room_kid: room,
			text,
			create_date: moment.utc().format(ChatConstants.dateFormat),
			update_date: moment.utc().format(ChatConstants.dateFormat),
			from_profil_kid: from,
			to_type: this.rooms.get(room).type,
		};

		if (file) {
			message.fileid = file.id;
		}

		if (isReplying) {
			message.reply_ref = isReplying.ref;
		}

		let newMessage = {};
		try {
			const result = await this.messageCreate(message);
			newMessage = result[result.elt_kid].data;
			newMessage.status = 'sent';

			/** @type {Room} */
			const myRoom = this.rooms.get(message.room_kid);

			console.log('room', myRoom);

			myRoom.listener.sendAll({
				type: EVENTS.CHAT.MESSAGE.CREATE,
				message: newMessage,
			});
		}
		catch (error) {
			console.error('errror', error);
			newMessage = {
				status: 'errored',
				...message,
			};
		}

		return newMessage;
	}

	/**
	 * Get the type of room from the room id
	 *
	 * @param {string} roomId
	 * @returns {'user'|'group'}
	 */
	// eslint-disable-next-line class-methods-use-this
	getTypeFromRoomId(roomId) {
		return new RegExp(`^K_NODE;.*?${ChatConstants.joinDMChar}K_NODE;.*?$`).test(roomId)
			? 'user'
			: 'group';
	}

	/**
	 * Set status of a thread
	 *
	 * @param {string} nodeKid
	 * @param {string} status
	 */
	setUserStatus(nodeKid, status) {
		this.statuses.set(nodeKid, status);
	}

	/**
	 *
	 * @param {string} roomKid
	 * @async
	 */
	async addRoomToRecent(roomKid) {
		this.d('Adding room ', roomKid, ' to the list of recent rooms');
		const room = this.rooms.get(roomKid);
		room.isRecent = true;
		// TODO Hack
		// Ensure a channel show unread notification when added to recents
		// Sometimes, the last access of the room, is a bit after the message creation
		room.lastAccess = moment.utc().subtract(5, 'second').format(ChatConstants.dateFormat);

		await this.saveWorkspace();
		return roomKid;
	}

	/**
	 * Triggered when submitting an MF search
	 * on the possible channels
	 *
	 * @param $item
	 * @async
	 */
	async onChatSearch($item) {
		if (!this.currentUser) {
			this.Notification.error({
				title: 'Erreur',
				message: 'Impossible de joindre le chat',
			});
			throw new Error('No current user');
		}

		if ($item.kid === this.currentUser.person.kid) {
			this.Notification.error({
				title: 'Erreur',
				message: 'Impossible d\'ajouter une conversation vers soi-même',
			});
			throw new Error('Can\'t add self conversation');
		}

		const type = $item.isa === 'K_NODE;KCHAT_GROUP' ? 'group' : 'user';

		console.log('$item.kid', $item.kid);

		/** @type String */
		const roomID = type === 'group' ? $item.kid : this.orderRoomIds($item.kid, this.currentUser.person.kid);

		let fetchRoom;
		console.log('this.rooms', this.rooms);
		/** @type Room */
		const roomExists = this.rooms.toArrayValues().find(value => value.id === roomID);
		if (roomExists) {
			fetchRoom = this.rooms.get(roomExists.ref);
		}
		else {
			/*
      // TODO in case room:create event progation doesn't work
      // I need to manually check if the room exist before creating it
      const rooms = await API.instance.RoomList(
        token,
        {
          filter: {
            id: room1.id,
          },
        },
      );
      console.log('rooms', rooms);
      // logger.info(
      // `${room1.from_profil_kid} -> ${room1.to_profil_kid}
      // (in ${room1.room_kid} ${room1.to_type}): ${room1.text}
      // `);
      let m;

      if (rooms.orders[0].elt_nb > 0) {
        m = Object.values(rooms.orders[0].elts)[0];
        console.log('room already exist, forwarding');
      } else {
        const response = await API.instance.RoomCreate(
          room1,
          token,
        );
        m = getResponse(response);
        socket.broadcast.emit(EVENTS.ROOM.CREATE m);
        logger.info('room created');
      }
       */
			// TODO rework
			const roomCreate = await this.roomCreate({
				data: '{}',
				type,
				id: roomID,
			});
			const createdRoom = roomCreate[roomCreate.elt_kid];
			fetchRoom = await this.importRoom(createdRoom.data);

			this.chatListener.sendToPerson($item.kid, {
				type: EVENTS.CHAT.ROOM.NEW,
				room: createdRoom,
			});
			await this.representations.fetchAll();
		}

		/** @type {Room} */
		try {
			const room = await fetchRoom;
			// console.log('room', room);
			this.rooms.set(room.ref, room);

			// TODO refactor for better oragnization
			if (room.isGroup()) {
				const node = await this.NodeService.load(roomID);
				try {
					if (node.checkDroit(this.Defaults.DROIT.READ)) {
						await this.addRoomToRecent(room.ref);
						console.log('a');
						await this.joinRoom(room);
						await this.changeRoom(room.ref);
						await this.fetchNewMessages(room.ref, fetchDirection.UP);
						return room.ref;
					}

					throw new Error('Invalid rights');
				}
				catch {
					this.Notification.error({
						title: 'Erreur',
						message: 'Vous n\'avez pas la permission d\'entrer dans ce salon',
					});
					return undefined;
				}
			}
			// When 1-1
			await this.addRoomToRecent(room.ref);
			console.log('b');
			await this.joinRoom(room);
			await this.changeRoom(room.ref);
			await this.fetchNewMessages(room.ref, fetchDirection.UP);
			return room.ref;
		}
		catch (error) {
			console.warn(error);
			return undefined;
		}
	}

	/**
	 * Send a notification
	 *
	 * @param {Message} message
	 */
	sendNotification(message) {
		let title = '';

		/** @type Room */
		const room = this.rooms.get(message.room_kid);

		if (room.isGroup()) {
			title = `${room.repre}: ${this.resolve(message.from_profil_kid, 'user')}`;
		}

		if (room.isOneToOne()) {
			title = this.resolve(message.from_profil_kid, 'user');
		}

		const options = {
			body: message.text,
			icon: '/favicon.ico',
		};
		// eslint-disable-next-line angular/window-service

		if (window?.DESKTOP) {
			// const notification = window?.DESKTOP?.sendNotification();
		}
		else {
			const notification = new window.Notification(title, options);
			notification.addEventListener('click', event => {
				event.preventDefault();
				window.focus();
				this.$timeout(() => {
					this.$location.url(`${ChatConstants.chatURL}?to=${message.room_kid}`);
				});
			});
		}
	}

	// eslint-disable-next-line class-methods-use-this
	/**
	 * @async
	 * @returns {Promise<NotificationPermission>}
	 */
	// eslint-disable-next-line class-methods-use-this
	registerNotifications() {
		if (!('Notification' in window)) {
			console.error('Ce navigateur ne supporte pas les notifications de bureau');
		}
		// eslint-disable-next-line angular/window-service
		if (window.Notification.permission !== 'denied') {
			return new Promise(resolve => {
				window.Notification.requestPermission((permission => resolve(permission)));
			});
		}
		return false;
	}

	/**
	 * @async
	 * @param {Message} _message
	 * @param {string} status
	 */
	async setMessageStatus(_message, status) {
		this.d('Updating message status', _message.ref, status);

		const result = await this.messageUpdate(_message.ref, {
			status,
		});

		const message = result[result.elt_kid].data;

		/** @type {Room} */
		const room = this.rooms.get(message.room_kid);
		return room.listener.sendAll({ type: EVENTS.CHAT.MESSAGE.UPDATE, message });
	}

	registerSockets() {
		// Socket

		const onOpen = async () => {
			this.status = 'online';
			try {
				await this.load();
				return true;
			}
			catch (error) {
				console.error('loading error', error);
				return false;
			}
		};

		const socket = this.WebsocketService.getWs();

		if (socket.readyState === 1) {
			onOpen();
		}

		socket.addEventListener('open', async () => {
			onOpen();
		});

		socket.addEventListener('close', () => {
			console.log('close');
			this.status = 'offline';
		});

		socket.addEventListener('error', () => {
			if (message === 'error') {
				const messages = {
					'xhr poll error': 'Impossible de joindre le serveur de chat',
				};
				this.Notification.error({
					title: 'Chat',
					message: messages[error.message] ?? error.message,
				});
			}
		});
	}

	/**
	 * @async
	 * @param kid
	 * @param name
	 * @returns {Promise<*>}
	 */
	removeAccessToConversation(kid, name) {
		this.d('Removing access to room', kid);
		this.rooms.get(kid).isLocked = true;
		const firstRoom = [...this.rooms.keys()][0];

		this.Notification.error({
			title: 'Accès supprimé',
			message: `Votre accès à "${name}" a été supprimé`,
		});
		return this.changeRoom(firstRoom);
	}

	async removeRoomFromRecent(roomReference) {
		this.d('Removing room from recents', roomReference);
		const room = this.rooms.get(roomReference);
		room.isRecent = false;
		room.lastAccess = moment.utc().format(ChatConstants.dateFormat);

		console.log('this.rooms', this.rooms);

		const firstRoom = [...this.rooms.keys()][0];

		console.log('this.rooms.keys()', this.rooms.keys());
		console.log('firstRoom', firstRoom);

		// TODO go to another one
		await this.changeRoom(firstRoom);
		await this.saveWorkspace();
		return roomReference;
	}

	// eslint-disable-next-line class-methods-use-this
	async reflect(promise) {
		try {
			const result = await promise;
			return {
				v: result,
				status: 'fulfilled',
			};
		}
		catch (error) {
			return {
				e: error,
				status: 'rejected',
			};
		}
	}

	debugValues() {
		console.log('this.listeners', this.listeners);
		console.log('this.rooms', this.rooms);
		console.log('this.messageManager', this.messageManager);
	}

	/**
	 *
	 * @param {Room} room
	 */
	async addListener(room) {
		const socket = await ws();
		const listener = socket.createRoom(room.getPath());

		listener
			.onJoined(message => {
				this.$rootScope.$apply(async () => {
					this.setUserStatus(message.person, 'online');
				});
			})
			.onLeaved(message => {
				this.$rootScope.$apply(async () => {
					this.setUserStatus(message.person, 'offline');
				});
			})
			.onList(message => {
				this.$rootScope.$apply(async () => {
					message.profiles.forEach(profile => {
						this.setUserStatus(profile.person, 'online');
					});
				});
			})
			.onMessage(_message => {
				console.log('onMessage', _message);

				const { payload } = _message;
				const { type } = payload;

				console.log('type', type);

				if (type === EVENTS.CHAT.MESSAGE.DELETE) {
					const { ref } = payload;
					return this.$rootScope.$evalAsync(async () => {
						this.messageManager.delete(ref);
					});
				}

				if (type === EVENTS.CHAT.MESSAGE.CREATE) {
					const { message: temporaryMessage } = payload;
					// const validator = MessageValidator.safeParse(temporaryMessage);

					// if (!validator.success) {
					// console.log(validator.error.format());
					// }

					// const parsedMessage = validator.data;

					const parsedMessage = temporaryMessage;

					this.$rootScope.$evalAsync(async () => {
						// eslint-disable-next-line promise/catch-or-return
						const importedMessage = await this.messageManager.import(parsedMessage);
						await this.representations.fetch(parsedMessage.from_profil_kid);

						if (
							!this.rooms.has(importedMessage.room_kid)
							|| !this.getRecentsRoom().has(importedMessage.room_kid)
						) {
							await this.addRoomToRecent(importedMessage.room_kid);
						}

						this.$rootScope.$broadcast(EVENTS.CHAT.MESSAGE.CREATE, importedMessage);

						// if thread is listed in recent
						// and if the message is not from yourself
						console.log('document.hasFocus()', document.hasFocus());
						console.log('two parts', importedMessage.from_profil_kid, this.currentUser.profil.kid);
						console.log('is conv selected', importedMessage.from_profil_kid, this.recipient);
						if (
						// Message not from you
							importedMessage.from_profil_kid !== this.currentUser.profil.kid && (
							// AND Document is not focused
								!document.hasFocus()
							// OR Document is focused but message is not from selected conversation
							|| (document.hasFocus() && importedMessage.room_kid !== this.recipient)
							)
						) {
							this.sendNotification(importedMessage);
						}

						return this.fetchUnreadMessages(importedMessage.room_kid);
					});
				}

				if (type === EVENTS.CHAT.MESSAGE.UPDATE) {
					const { message: temporaryMessage } = payload;
					// console.log('MessageValidator', MessageValidator);
					// const validator = MessageValidator.safeParse(temporaryMessage);

					// if (validator.sucess) {
					// const parsedMessage = validator.data;
					const parsedMessage = temporaryMessage;

					return this.$rootScope.$evalAsync(async () => this.$q.when(this.messageManager.import(parsedMessage)));
					// }
					// else {
					// console.error(validator.error.format());
					// }
				}

				if (type === EVENTS.CHAT.ROOM.NEW) {
					return this.$rootScope.$evalAsync(async () => {
						const createdRoom = await this.importRoom(payload.room);
						await this.representations.fetchAll();
						if (createdRoom) {
							this.rooms.set(createdRoom.ref, createdRoom);
							if (createdRoom.isOneToOne()) {
								await this.joinRoom(createdRoom);
							}
						}
					});
				}

				return true;
			})
			.subscribe();

		room.setListener(listener);
	}

	/**
	 *
	 * @param {Room} room
	 */
	async joinRoom(room) {
		this.d('Joining room', room);

		return this.addListener(room);
	}

	async onLoadReceived() {
		const socket = await ws();
		this.chatListener = socket.createRoom('/chat');

		this.chatListener
			.onMessage(_message => {
				const { type } = _message;

				if (type === EVENTS.CHAT.ROOM.NEW) {
					/** @type {*} */
					this.$rootScope.$apply(async () => {
						const createdRoom = await this.importRoom(room);
						await this.representations.fetchAll();
						if (createdRoom) {
							this.rooms.set(createdRoom.ref, createdRoom);
							if (createdRoom.isOneToOne()) {
								console.log('d');
								await this.joinRoom(createdRoom);
							}
						}
					});
				}
			})
			.subscribe();

		const { kid } = this.currentUser.person;

		// Settings ---
		// TODO save profile to localstorage
		const settingsList = await this.settingsList({
			filter: {
				SQL_OR: {
					person_kid: kid,
				},
			},
		});
		const profiles = settingsList.elts;

		let settings;
		if (profiles) {
			settings = Object.values(profiles)[0];
		}

		if (!settings) {
			console.info(`No profiles found. Creating...${kid}`);
			const create = await this.settingsCreate({ data: '{}', person_kid: kid });
			const { elt_kid: elementKid } = create;
			console.log('create', create);
			settings = create[elementKid].data;
		}

		// Parse data which is JSON
		if (settings.data) {
			settings.data = JSON.parse(settings.data);
		}

		// To save back later
		this.settingsRef = settings.ref;
		// Settings ---

		// Rooms ---

		const roomList = await this.roomList();
		const rooms = roomList.elts;

		// TODO duplicates ?
		await this.setRooms(rooms);

		const { rooms: recentRoomsFromSettings } = settings.data;
		if (recentRoomsFromSettings) {
			// eslint-disable-next-line unicorn/no-array-for-each
			Object.entries(recentRoomsFromSettings).forEach(([key, conversation]) => {
				if (this.rooms.has(key)) {
					const room = this.rooms.get(key);
					room.isRecent = true;
					// room.isLoading = true;
					room.lastAccess = conversation.lastAccess;
					room.isSelected = conversation.isSelected;
				}
			});
		}
		// Rooms ---

		// TODO Join all rooms in parallel
		const iterator = this.rooms.values();
		for (const room of iterator) {
			await this.joinRoom(room);
		}

		const recentRooms = this.getRecentsRoom();
		if (recentRooms.size > 0) {
			const threadPromises = [];
			for (const [, room] of recentRooms) {
				if (room.isGroup()) {
					threadPromises.push(this.NodeService.load(room.id));
				}
			}

			const nodes = await Promise.all(threadPromises.map(this.reflect));
			const actions = [];
			// eslint-disable-next-line no-restricted-syntax
			for (const node of nodes) {
				if (node.status === 'fulfilled') {
					const nodeThread = node.v;
					if (!nodeThread.checkDroit(this.Defaults.DROIT.READ)) {
						actions.push(this.removeAccessToConversation(nodeThread.kid, nodeThread.name));
					}
				}
				else {
					// TODO attention
					// const nodeError = node.e;
					// const id = nodeError.errno_desc.match(/id \((.*?)\)/)[1];
					// if (this.chat?.threads?.[id]?.repre) {
					// 	const name = this.threads[id].repre;
					// 	actions.push(this.removeAccessToConversation(id, name));
					// }
				}
			}

			await Promise.all(actions);
			if (this.getRecentsRoom().size > 0) {
				// Find which chat is selected
				let selectedConversation = null;
				for (const [key, conversation] of this.getRecentsRoom().entries()) {
					if (conversation.isSelected) {
						selectedConversation = key;
					}
				}

				const isChatPage = this.$location.path() === ChatConstants.chatURL;

				const promises = [];
				for (const [, room] of this.getRecentsRoom()) {
					promises.push(async () => {
						await this.fetchNewMessages(room.ref, fetchDirection.UP);
						this.fetchUnreadMessages(room.ref);
						// If chat is selected in this.workspace, select it
						if (selectedConversation && isChatPage) {
							await this.changeRoom(selectedConversation);
						}
					});
				}

				await this.$q.all(promises.map(prom => prom()));
			}
		}
		// ---
	}

	async load() {
		this.isReady = false;
		await this.onLoadReceived();
		this.isReady = true;
		this.$rootScope.$broadcast(EVENTS.CHAT.READY);
		return true;
	}
}
